import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaUserPlus,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaEnvelope,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import styles from './JoinClubModal.module.css';

const JoinClubModal = ({ club, onClose, onJoinSuccess, user }) => {
  
  const [isLoading, setIsLoading] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { userProfile } = useUser();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Check if user is already a member
  useEffect(() => {
    if (club && user && userProfile) {
      checkMembershipStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club, user, userProfile]);

  const checkMembershipStatus = async () => {
    if (!club || !club._id || !userProfile || !userProfile._id) return;
    
    try {
      const url = `${backendUrl}/api/clubs/${club._id}/membership/${userProfile._id}`;
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setMembershipStatus(data.status); // 'active', 'pending', etc.
      }
    } catch (error) {
      // Silently fail - membership check is not critical
    }
  };

  const handleJoinClub = async () => {
    if (!club || !club._id) {
      setError('Club information is missing. Please try again.');
      return;
    }
    
    if (!userProfile || !userProfile._id) {
      setError('User profile is not loaded. Please refresh the page and try again.');
      return;
    }

    if (!backendUrl) {
      setError('Backend URL is not configured. Please check your environment settings.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const requestBody = {
        userId: userProfile._id,
        userEmail: user.email,
        userName: user.displayName || user.email,
      };

      const response = await fetch(`${backendUrl}/api/clubs/${club._id}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to join club');
      }

      // Update membership status based on response
      setMembershipStatus(data.status);
      
      // Show appropriate success message based on membership type
      if (club.membershipType === 'free_open') {
        setSuccessMessage('Successfully joined the club! You now have access to all club features and programs.');
        setShowSuccessMessage(true);
        // Don't call onJoinSuccess here - let the user see the success message first
      } else if (club.membershipType === 'free_approval') {
        setSuccessMessage('Your join request has been submitted successfully! Please wait for approval from the Club Manager.');
        setShowSuccessMessage(true);
      } else if (club.membershipType === 'paid') {
        setSuccessMessage(`Your join request has been submitted successfully! Please complete the payment of $${club.membershipFee || 0} through our portal to finalize your membership.`);
        setShowSuccessMessage(true);
      }
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setError(error.message || 'Failed to join club. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getMembershipTypeInfo = () => {
    switch (club.membershipType) {
      case 'free_open':
        return {
          title: 'Free - Open to Everyone',
          description: 'Anyone can join this club without approval.',
          icon: <FaCheckCircle className={styles.infoIcon} />,
          color: 'green',
        };
      case 'free_approval':
        return {
          title: 'Free - Approval Required',
          description: 'Join request will be sent to club admin for approval.',
          icon: <FaClock className={styles.infoIcon} />,
          color: 'orange',
        };
      case 'paid':
        return {
          title: 'Paid Membership',
          description: `Annual membership fee: $${club.membershipFee || 0}`,
          icon: <FaCreditCard className={styles.infoIcon} />,
          color: 'blue',
        };
      default:
        return {
          title: 'Free - Open to Everyone',
          description: 'Anyone can join this club without approval.',
          icon: <FaCheckCircle className={styles.infoIcon} />,
          color: 'green',
        };
    }
  };

  const membershipInfo = getMembershipTypeInfo();

  // If user is already a member, show different content
  if (membershipStatus === 'member') {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <FaUserPlus className={styles.modalTitleIcon} />
              Already a Member
            </div>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.successMessage}>
              <FaCheckCircle className={styles.successIcon} />
              <h3>You are already a member of {club.name}</h3>
              <p>You can access all club features and programs.</p>
            </div>
            <div className={styles.buttonContainer}>
              <button className={styles.enterButton} onClick={onClose}>
                Enter Club
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If membership is pending
  if (membershipStatus === 'pending') {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <FaClock className={styles.modalTitleIcon} />
              Membership Pending
            </div>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.pendingMessage}>
              <FaClock className={styles.pendingIcon} />
              <h3>Your membership request is pending</h3>
              <p>
                {club.membershipType === 'paid' 
                  ? 'Please complete payment and wait for admin approval.'
                  : 'Please wait for admin approval to join the club.'
                }
              </p>
              {club.membershipType === 'paid' && (
                <div className={styles.paymentInfo}>
                  <p><strong>Membership Fee:</strong> ${club.membershipFee || 0}/year</p>
                  <p>You will receive payment instructions via email.</p>
                </div>
              )}
            </div>
            <div className={styles.buttonContainer}>
              <button className={styles.closeButton} onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user has been invited but hasn't accepted yet
  if (membershipStatus === 'invited') {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <FaEnvelope className={styles.modalTitleIcon} />
              You've Been Invited!
            </div>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.pendingMessage}>
              <FaEnvelope className={styles.pendingIcon} style={{ color: '#A4EB3F' }} />
              <h3>You've been invited to join {club.name}</h3>
              <p>
                Click "Join Club" below to accept the invitation and become a member.
              </p>
            </div>
            <div className={styles.buttonContainer}>
              <button
                className={`${styles.joinButton} ${isLoading ? styles.loading : ''}`}
                onClick={handleJoinClub}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Join Club'}
              </button>
              <button className={styles.cancelButton} onClick={onClose}>
                Not Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If showing success message after join request
  if (showSuccessMessage) {
    const handleCloseSuccess = () => {
      setShowSuccessMessage(false);
      // For free_open memberships, call onJoinSuccess when closing the success message
      if (club.membershipType === 'free_open') {
        onJoinSuccess && onJoinSuccess();
      }
      onClose();
    };

    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <FaCheckCircle className={styles.modalTitleIcon} />
              Success!
            </div>
            <button className={styles.closeButton} onClick={handleCloseSuccess} aria-label="Close">
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.successMessage}>
              <FaCheckCircle className={styles.successIcon} />
              <h3>Request Submitted Successfully!</h3>
              <p>{successMessage}</p>
            </div>
            <div className={styles.buttonContainer}>
              <button className={styles.enterButton} onClick={handleCloseSuccess}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  
  // Safety check - if club is not properly defined, show error
  if (!club || !club._id) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <FaUserPlus className={styles.modalTitleIcon} />
              Error
            </div>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.errorMessage}>
              <FaExclamationTriangle className={styles.errorIcon} />
              <p>Club information is missing. Please try again.</p>
            </div>
            <div className={styles.buttonContainer}>
              <button className={styles.cancelButton} onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FaUserPlus className={styles.modalTitleIcon} />
            Join {club.name}
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className={styles.modalBody}>
          {/* Club Info */}
          <div className={styles.clubInfo}>
            <img
              src={club.clubLogo || '/placeholder-logo.png'}
              alt={`${club.name} Logo`}
              className={styles.clubLogo}
            />
            <h3>{club.name}</h3>
            <p className={styles.clubDescription}>{club.description}</p>
          </div>

          {/* Membership Type Info */}
          <div className={`${styles.membershipInfo} ${styles[membershipInfo.color]}`}>
            {membershipInfo.icon}
            <div>
              <h4>{membershipInfo.title}</h4>
              <p>{membershipInfo.description}</p>
            </div>
          </div>

          {/* Payment Details for Paid Membership - Currently not displayed */}

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              <FaExclamationTriangle className={styles.errorIcon} />
              <p>{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.buttonContainer}>
            {/* Only show "Enter Club" if user has ACTIVE membership status */}
            {(membershipStatus === 'active' || membershipStatus === 'approved' || membershipStatus === 'member') ? (
              <button
                className={styles.enterButton}
                onClick={onClose}
              >
                Enter Club
              </button>
            ) : (
              <button
                className={`${styles.joinButton} ${isLoading ? styles.loading : ''}`}
                onClick={handleJoinClub}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : (membershipStatus === 'inactive' ? 'Rejoin Club' : 'Become Member')}
              </button>
            )}
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinClubModal; 